import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminUsersApi } from '../services/adminUsersApi';
import type { AdminUserResponse } from '../types';
import { useAuth } from '../hooks/useAuth';
import CursorPagination from '../components/CursorPagination';
import UsernameChip from '../components/UsernameChip';

function RoleBadge({ userType }: { userType: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    ADMIN: { cls: 'admin', label: 'Admin' },
    GARAGE_OWNER: { cls: 'garage-owner', label: 'Garage Owner' },
    CUSTOMER: { cls: 'approved', label: 'Customer' },
  };
  const { cls, label } = map[userType] || { cls: '', label: userType };
  return <span className={`admin-badge ${cls}`}>{label}</span>;
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'BLOCKED' ? 'rejected' : 'approved';
  return <span className={`admin-badge ${cls}`}>{status}</span>;
}

export default function UsersPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState<AdminUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  const searchText = searchParams.get('search') || '';
  const roleFilter = searchParams.get('role') || '';
  const statusFilter = searchParams.get('status') || '';

  const updateParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params, { replace: true });
  };

  const fetchUsers = useCallback((cursor?: string) => {
    if (!auth.token) return;
    const isLoadMore = !!cursor;
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    adminUsersApi
      .listUsers(
        {
          search: searchText || undefined,
          role: roleFilter || undefined,
          status: statusFilter || undefined,
          cursor,
          size: 20,
        },
        auth.token,
      )
      .then((data) => {
        if (isLoadMore) {
          setUsers((prev) => [...prev, ...data.content]);
        } else {
          setUsers(data.content);
        }
        setNextCursor(data.nextCursor || undefined);
        setHasMore(data.hasMore);
      })
      .catch((err) => setError(err.message))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, [auth.token, searchText, roleFilter, statusFilter]);

  useEffect(() => {
    const timeout = setTimeout(() => fetchUsers(), 300);
    return () => clearTimeout(timeout);
  }, [fetchUsers]);

  const handleLoadMore = () => {
    if (nextCursor) fetchUsers(nextCursor);
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', marginBottom: '1.25rem' }}>
        Users
      </h2>

      <div className="admin-card">
        <div className="admin-toolbar" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search by username, name, email, or phone..."
            value={searchText}
            onChange={(e) => updateParam('search', e.target.value)}
          />
          <select
            className="admin-select"
            value={roleFilter}
            onChange={(e) => updateParam('role', e.target.value)}
          >
            <option value="">All Roles</option>
            <option value="CUSTOMER">Customer</option>
            <option value="GARAGE_OWNER">Garage Owner</option>
            <option value="ADMIN">Admin</option>
          </select>
          <select
            className="admin-select"
            value={statusFilter}
            onChange={(e) => updateParam('status', e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="BLOCKED">Blocked</option>
          </select>
        </div>

        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
          </div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : users.length === 0 ? (
          <div className="admin-empty-state">
            <p>No users found.</p>
          </div>
        ) : (
          <>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Registered</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td style={{ fontWeight: 500 }}>{user.name}</td>
                      <td><UsernameChip username={user.username} userId={user.id} compact /></td>
                      <td>{user.email || '-'}</td>
                      <td>{user.phone || '-'}</td>
                      <td><RoleBadge userType={user.userType} /></td>
                      <td><StatusBadge status={user.status} /></td>
                      <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}</td>
                      <td>
                        <button
                          className="admin-btn admin-btn-outline admin-btn-sm"
                          onClick={() => navigate(`/users/${user.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CursorPagination hasMore={hasMore} loading={loadingMore} onLoadMore={handleLoadMore} />
          </>
        )}
      </div>
    </div>
  );
}
